import i18n from '../../../../../../i18n';
import { formatDrillUnit, fmt } from '../../../../data/metrics';
import { sourceMeta } from '../../../../data/reservationSources';
import type { DriverBucket, DriverDimension } from '../../../../types';
import type { ReservationSourceId } from '../../../../types';

const SHARE_COLORS = [
  'var(--accent)',
  'var(--st-steady)',
  'var(--st-busy)',
  'var(--petrol-ink-light, #14606B)',
  'var(--st-quiet)',
  'var(--ink-3)',
];

export function dimFmt(dim: DriverDimension, v: number, fallback: (n: number) => string): string {
  return dim.unit ? formatDrillUnit(v, dim.unit) : fallback(v);
}

/** Muted "n=15" suffix for a bucket's reservation sample. */
export function sampleLabel(sample: number): string {
  return i18n.t('performance:drillUi.sampleN', { n: fmt.num(sample) });
}

/** Too few reservations, or a rate gap inside normal variation. */
export function isMutedBucket(b: DriverBucket): boolean {
  return !!b.thin || b.significant === false;
}

/** Tooltip for a muted bucket; empty when the bucket reads as a driver. */
export function mutedBucketTitle(b: DriverBucket): string {
  if (b.thin) return i18n.t('performance:drillUi.thinSample');
  if (b.significant === false) return i18n.t('performance:drillUi.notSignificant');
  return '';
}

/**
 * `dimFmt` plus the bucket's "· n=15" for chart value labels, which only see
 * the drawn value. Ambiguous values (two buckets, different samples) and
 * values that are not a bucket (baseline, compare) get no suffix.
 */
export function dimFmtWithSample(
  dim: DriverDimension,
  v: number,
  fallback: (n: number) => string,
): string {
  const base = dimFmt(dim, v, fallback);
  const samples = new Set(
    dim.buckets.filter(b => Math.abs(b.value) === Math.abs(v)).map(b => b.sample),
  );
  if (samples.size !== 1) return base;
  const [sample] = [...samples];
  return `${base} · ${sampleLabel(sample)}`;
}

/** Signed percent-change of the two displayed bucket values. */
export function pctChange(current: number, compare: number | null | undefined): number | null {
  if (compare == null || compare === 0) return null;
  return +(((current / compare) - 1) * 100).toFixed(1);
}

export function fmtPctChange(current: number, compare: number | null | undefined): string {
  const d = pctChange(current, compare);
  if (d == null) return '';
  return `${d >= 0 ? '+' : ''}${d}%`;
}

export function dimTotal(
  dim: DriverDimension,
  which: 'primary' | 'secondary' = 'primary',
): number {
  return dim.buckets.reduce(
    (s, b) => s + (which === 'primary' ? b.value : b.value2 ?? 0),
    0,
  );
}

function isMoneyUnit(unit?: string): boolean {
  if (!unit) return false;
  if (unit === 'eur' || unit === '€' || unit === '£' || unit === '$') return true;
  return /^[A-Z]{3}$/.test(unit);
}

function formatAvgDelta(dim: DriverDimension, diff: number): string {
  if (diff === 0) return dim.unit === 'min' ? '0m' : '0';
  const sign = diff > 0 ? '+' : '−';
  if (dim.unit === 'min') return `${sign}${Math.abs(diff)}m`;
  if (isMoneyUnit(dim.unit)) {
    return `${sign}${formatDrillUnit(Math.abs(diff), dim.unit)}`;
  }
  return `${sign}${Math.abs(diff)}`;
}

/** Share % / ±pt / avg delta for a bucket vs dim baseline or total. */
export function driverBucketContext(
  dim: DriverDimension,
  bucket: { value: number; sharePct?: number | null },
): string {
  const value = bucket.value;
  if (dim.mode === 'share') {
    if (bucket.sharePct != null) return `${Math.round(bucket.sharePct)}%`;
    const total = dimTotal(dim);
    if (total <= 0) return '';
    return `${Math.round((Math.abs(value) / total) * 100)}%`;
  }
  if (dim.mode === 'rate') {
    const diff = +(value - dim.baseline).toFixed(1);
    if (diff === 0) return i18n.t('performance:drillUi.atAvg');
    return i18n.t('performance:drillUi.ptDelta', { delta: `${diff > 0 ? '+' : ''}${diff}` });
  }
  if (dim.mode === 'avg') {
    const diff = Math.round(value - dim.baseline);
    if (diff === 0) return i18n.t('performance:drillUi.atAvg');
    return formatAvgDelta(dim, diff);
  }
  return '';
}

export function driverTakeaway(dim: DriverDimension): string | null {
  const top = dim.topDriver;
  const bottom = dim.bottomDriver;
  if (!top || !bottom || top.label === bottom.label) return null;
  if (dim.mode === 'share') {
    const topCtx = driverBucketContext(dim, top);
    const bottomCtx = driverBucketContext(dim, bottom);
    if (dim.shareOf === 'occupancy') {
      return i18n.t('performance:drillUi.takeawayOccupancy', {
        top: top.label,
        topCtx: topCtx || i18n.t('performance:drillUi.most'),
        bottom: bottom.label,
        bottomCtx: bottomCtx ? ` (${bottomCtx})` : '',
      });
    }
    return i18n.t('performance:drillUi.takeawayVolume', {
      top: top.label,
      topCtx: topCtx ? ` (${topCtx})` : '',
      bottom: bottom.label,
      bottomCtx: bottomCtx ? ` (${bottomCtx})` : '',
    });
  }
  const topCtx = driverBucketContext(dim, top);
  const bottomCtx = driverBucketContext(dim, bottom);
  return i18n.t('performance:drillUi.takeawayRate', {
    top: top.label,
    topCtx: topCtx ? ` (${topCtx})` : '',
    bottom: bottom.label,
    bottomCtx: bottomCtx ? ` (${bottomCtx})` : '',
  });
}

/** Dim with the strongest top/bottom signal — used for multi-dim crux. */
export function strongestDimension(dims: DriverDimension[]): DriverDimension {
  if (dims.length <= 1) return dims[0];
  let best = dims[0];
  let bestScore = -1;
  for (const dim of dims) {
    const hib = dim.higherIsBetter != null ? dim.higherIsBetter : dim.mode !== 'rate';
    const focus =
      dim.mode === 'rate'
        ? hib
          ? dim.bottomDriver
          : dim.topDriver
        : dim.mode === 'avg'
          ? (() => {
              let o = dim.topDriver;
              if (
                dim.bottomDriver &&
                (!o ||
                  Math.abs(dim.bottomDriver.vsBaselinePct ?? 0) >
                    Math.abs(o.vsBaselinePct ?? 0))
              ) {
                o = dim.bottomDriver;
              }
              return o;
            })()
          : dim.topDriver;
    const score = focus
      ? dim.mode === 'share'
        ? (focus.sharePct ?? 0)
        : Math.abs(focus.vsBaselinePct ?? 0)
      : 0;
    if (score > bestScore) {
      bestScore = score;
      best = dim;
    }
  }
  return best;
}

export function higherIsBetter(dim: DriverDimension): boolean {
  if (dim.higherIsBetter != null) return dim.higherIsBetter;
  return dim.mode !== 'rate';
}

export function outlierTone(
  dim: DriverDimension,
  bucket: DriverBucket | null,
  prefer: 'top' | 'bottom',
): 'good' | 'bad' | 'neutral' {
  if (!bucket) return 'neutral';
  const hib = higherIsBetter(dim);
  if (prefer === 'top') {
    // topDriver is highest value
    return hib ? 'good' : 'bad';
  }
  return hib ? 'bad' : 'good';
}

export function shareColor(i: number): string {
  return SHARE_COLORS[i % SHARE_COLORS.length];
}

export function sourceColor(label: string): string {
  const known = [
    'widget',
    'reserveWithGoogle',
    'assistentAi',
    'staff',
    'walkIn',
    'integration',
    'other',
  ] as ReservationSourceId[];
  const match = known.find(
    id => sourceMeta(id).label.toLowerCase() === label.toLowerCase(),
  );
  if (match) return sourceMeta(match).color;
  return 'var(--accent)';
}

export function dedupeDimensions(dimensions: DriverDimension[]): DriverDimension[] {
  const hasDay = dimensions.some(d => d.id === 'daypart');
  const hasRegime = dimensions.some(d => d.id === 'regime');
  if (hasDay && hasRegime) {
    return dimensions.filter(d => d.id !== 'regime');
  }
  return dimensions;
}

export function bucketSharePct(dim: DriverDimension, value: number): number {
  const total = dim.buckets.reduce((a, b) => a + Math.abs(b.value), 0);
  if (!total) return 0;
  return +((Math.abs(value) / total) * 100).toFixed(0);
}
